/**
 * Difficulty helpers — single source of truth for the value ⇆ URL
 * mapping.
 *
 * The content model stores three difficulty values:
 *   'beginner' | 'intermediate' | 'advanced'
 *
 * (The 'beginner' value is labelled "Easy" in the UI, and the
 * 'expert' tier was dropped — both 2026-05 client changes. The
 * stored value stays 'beginner' for backwards compatibility with
 * GROQ filters and existing documents.)
 *
 * The public URL param, however, reads "easy" for that tier:
 *   ?difficulty=easy | intermediate | advanced
 *
 * These two functions bridge the two representations. Anything that
 * builds a difficulty link uses `difficultyValueToParam`; anything
 * that reads `?difficulty=` back off the URL uses
 * `difficultyParamToValue`.
 */

/** Canonical stored difficulty values. */
export const DIFFICULTY_VALUES = ['beginner', 'intermediate', 'advanced'] as const
export type DifficultyValue = (typeof DIFFICULTY_VALUES)[number]

/** Stored value → public URL param. */
export function difficultyValueToParam(value: string): string {
  return value === 'beginner' ? 'easy' : value
}

/**
 * Public URL param → stored value. Returns `null` for an absent or
 * unrecognized param so callers can fall back to "no filter".
 *
 * Accepts the legacy `?difficulty=beginner` form too — old links,
 * bookmarks, and the pre-2026-05 `?difficulty=expert` are tolerated
 * (expert collapses to advanced, matching the data migration).
 */
export function difficultyParamToValue(
  param: string | null | undefined,
): DifficultyValue | null {
  if (!param) return null
  const normalized = param.trim().toLowerCase()
  switch (normalized) {
    case 'easy':
    case 'beginner':
      return 'beginner'
    case 'intermediate':
      return 'intermediate'
    case 'advanced':
    case 'expert': // legacy — Expert tier was folded into Advanced
      return 'advanced'
    default:
      return null
  }
}
